import { CircularProgress, Grid } from '@mui/material';
import { HSpacer, RoutalDialog, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { toDayNoon } from '@nx-smartmonkey/shared/helpers';
import { ProjectPlans } from '../../../domain/project/ProjectPlans';
import useCreateEmptyPlan from '../../../hooks/plan/useCreateEmptyPlan';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import Button, { ButtonProps } from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import { InputDatePicker } from '../../../react-components/input-date-picker/InputDatePicker';

type CreatePlanProps = {
  buttonProps: ButtonProps;
  handleCreatePlan: (newPlanId: string) => void;
};

export const CreatePlan = ({ buttonProps, handleCreatePlan }: CreatePlanProps) => {
  const { data: user } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [executionDate, setExecutionDate] = useState<Date>(toDayNoon(new Date()));
  const [label, setLabel] = useState<string>(``);
  const {
    run: createEmptyPlan,
    data: createdPlanId,
    success: planCreated,
    isLoading: isCreating,
  } = useCreateEmptyPlan({ project: activeProject });

  useEffect(() => {
    if (activeProject) {
      setLabel(
        ProjectPlans.labelTemplateToValue({
          template: activeProject.plans.label,
          date: new Date(),
          language: user?.language.value,
        })
      );
      setExecutionDate(activeProject.plans.defaultStartDateToValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (planCreated && createdPlanId) {
      handleCreatePlan(createdPlanId);
    }
  }, [createdPlanId, handleCreatePlan, planCreated]);

  if (!activeProject) return null;

  const handleCreateEmptyPlan = () => {
    createEmptyPlan({
      label,
      status: activeProject.plans.status,
      executionDate: executionDate ?? activeProject.plans.defaultStartDateToValue(),
    });
  };

  return (
    <>
      <Button
        {...buttonProps}
        disabled={isCreating || dialogOpen}
        onClick={() => {
          if (activeProject.plans.show_dialog_on_create) {
            setDialogOpen(true);
          } else {
            handleCreateEmptyPlan();
          }
        }}
      >
        {isCreating && (
          <>
            <Grid container direction="column" justifyContent="center" style={{ height: `100%`, width: `32px` }}>
              <div className="lds-ellipsis-little" style={{ height: `8px` }}>
                <div style={{ top: `2px`, backgroundColor: `white` }} />
                <div style={{ top: `2px`, backgroundColor: `white` }} />
                <div style={{ top: `2px`, backgroundColor: `white` }} />
              </div>
            </Grid>
            <HSpacer small />
          </>
        )}
        <Text
          tagName="span"
          variant="body"
          style={{ color: `white`, cursor: `pointer` }}
          intlId="planner.plans.create_new_plan"
        />
      </Button>

      <RoutalDialog
        id="create-plan-dialog"
        onCloseDialog={() => setDialogOpen(false)}
        open={dialogOpen}
        contentWithOverflow={false}
        contentStyles={{
          height: `100%`,
        }}
        titleContent={<Text variant="h6" intlId="create_plan_dialog.title" />}
        titleRightContent={
          <Button
            disabled={isCreating}
            style={{
              minWidth: `125px`,
            }}
            onClick={() => {
              handleCreateEmptyPlan();
            }}
          >
            {isCreating ? (
              <CircularProgress size={32} />
            ) : (
              <Text variant="button" intlId="create_plan_dialog.button_create" style={{ color: `white` }} />
            )}
          </Button>
        }
        showFooter={false}
      >
        <Grid container direction="column" style={{ height: `100%` }}>
          <InputText
            label={<Text variant="label" intlId="create_plan_dialog.label" />}
            type="text"
            fullWidth
            fieldValue={label}
            onChange={(text: any) => {
              setLabel(text);
            }}
            autoFocus
          />

          <VSpacer medium />

          <InputDatePicker
            labelIntlId="create_plan_dialog.execution_date"
            dateValue={executionDate}
            onChange={(date: Date) => {
              setExecutionDate(toDayNoon(date));
            }}
            locale={user?.language.value}
            portalId="create-plan-dialog"
          />
        </Grid>
      </RoutalDialog>
    </>
  );
};
